import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { signIn, signUp, signOut as cognitoSignOut, getCurrentUser, confirmSignUp } from 'aws-amplify/auth';
import type { UserIdentity } from '../types';
import * as api from './api';

type Status = 'loading' | 'unauthenticated' | 'authenticated' | 'confirm_signup';

interface AuthCtx {
  status: Status;
  user: UserIdentity | null;
  email: string | null;
  register: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  confirmRegistration: (email: string, code: string) => Promise<{ ok: boolean; error?: string }>;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  joinWithCode: (code: string) => Promise<boolean>;
  updateUser: (u: Partial<UserIdentity>) => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

const IDENTITY_KEY = 'ae_user';
const EMAIL_KEY = 'ae_email';

function saveIdentity(u: UserIdentity) { localStorage.setItem(IDENTITY_KEY, JSON.stringify(u)); }
function loadIdentity(): UserIdentity | null {
  try { const r = localStorage.getItem(IDENTITY_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
function clearIdentity() { localStorage.removeItem(IDENTITY_KEY); localStorage.removeItem(EMAIL_KEY); }
function saveEmail(email: string) { localStorage.setItem(EMAIL_KEY, email); }
function loadEmail(): string | null { return localStorage.getItem(EMAIL_KEY); }

function emailToUserId(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = ((hash << 5) - hash) + email.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<UserIdentity | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const currentUser = await getCurrentUser();
        const storedEmail = currentUser.signInDetails?.loginId || loadEmail();
        setEmail(storedEmail || null);

        const stored = loadIdentity();
        if (stored?.access_key) {
          setUser(stored);
          setStatus('authenticated');
        } else if (storedEmail) {
          const deviceId = emailToUserId(storedEmail);
          const identity = await api.initializeUser(deviceId, storedEmail);
          if (identity) {
            saveIdentity(identity);
            saveEmail(storedEmail);
            setUser(identity);
            setStatus('authenticated');
          } else {
            setStatus('unauthenticated');
          }
        } else {
          setStatus('unauthenticated');
        }
      } catch {
        setStatus('unauthenticated');
      }
    })();
  }, []);

  const register = useCallback(async (regEmail: string, password: string) => {
    try {
      const result = await signUp({
        username: regEmail,
        password,
        options: { userAttributes: { email: regEmail } },
      });
      setEmail(regEmail);
      saveEmail(regEmail);
      if (result.nextStep?.signUpStep === 'CONFIRM_SIGN_UP') {
        setStatus('confirm_signup');
      }
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || 'Registration failed' };
    }
  }, []);

  const confirmRegistration = useCallback(async (confEmail: string, code: string) => {
    try {
      await confirmSignUp({ username: confEmail, confirmationCode: code });
      setStatus('unauthenticated'); // User needs to sign in after confirming
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || 'Confirmation failed' };
    }
  }, []);

  const login = useCallback(async (loginEmail: string, password: string) => {
    try {
      try { await cognitoSignOut(); } catch {}

      const result = await signIn({ username: loginEmail, password });

      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        setEmail(loginEmail);
        saveEmail(loginEmail);
        setStatus('confirm_signup');
        return { ok: true };
      }

      if (!result.isSignedIn) {
        return { ok: false, error: 'Sign in was not completed' };
      }

      setEmail(loginEmail);
      saveEmail(loginEmail);

      const deviceId = emailToUserId(loginEmail);
      const identity = await api.initializeUser(deviceId, loginEmail);
      if (identity) {
        saveIdentity(identity);
        setUser(identity);
        setStatus('authenticated');
        return { ok: true };
      }
      return { ok: false, error: 'Failed to initialize your account' };
    } catch (err: any) {
      return { ok: false, error: err?.message || 'Login failed' };
    }
  }, []);

  const joinWithCode = useCallback(async (code: string) => {
    const identity = await api.redeemTransferCode(code);
    if (identity) {
      clearIdentity();
      saveIdentity(identity);
      setUser(identity);
      setStatus('authenticated');
      return true;
    }
    return false;
  }, []);

  const updateUser = useCallback((updates: Partial<UserIdentity>) => {
    setUser(prev => {
      if (!prev) return prev;
      const u = { ...prev, ...updates };
      saveIdentity(u);
      return u;
    });
  }, []);

  const handleSignOut = useCallback(async () => {
    try { await cognitoSignOut(); } catch {}
    clearIdentity();
    setUser(null);
    setEmail(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({ status, user, email, register, confirmRegistration, login, joinWithCode, updateUser, signOut: handleSignOut }),
    [status, user, email, register, confirmRegistration, login, joinWithCode, updateUser, handleSignOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
